import { useQuery } from '@apollo/react-hooks';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  createMuiTheme,
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import gql from 'graphql-tag';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { ProgressBar } from 'react-native-paper';
import MaterialIcon from 'react-native-vector-icons/MaterialIcons';
import { useParams } from 'react-router-dom';
import DateRangeModal, {
  customDateRangeDisplay,
  DateRange,
} from 'shared/components/DateRangeModal';
import ErgIcon from 'shared/components/ErgIcon';
import Logo from 'shared/components/Logo';
import { green, red } from 'shared/components/metrics';
import { t } from 'shared/i18n';
import { ErgType, TrackRole } from 'shared/types';
import {
  calculateCalsHour,
  calculatePace,
  calculatePower,
  calculateProgressFromPrevious,
  ergTypeDisplay,
  formatTime,
  humanizeDistance,
  statsEmojis,
  userColor,
} from 'shared/utils';
import { muiThemeAttributes } from 'utils/helpers';
import { isMobileApp, postMobileMessage } from 'utils/mobile';

const GET_STATS = gql`
  query GetTrackStats(
    $trackIds: [ID!]
    $trackId: ID!
    $time: String!
    $ergTypes: [String]
    $startDate: String
    $endDate: String
    $timezone: String
    $skipPreviousStats: Boolean!
  ) {
    track(id: $trackId) {
      id
      role
      source {
        id
        name
        imageUrl
        insertedAt
      }
      tracksFromSource {
        id
        name
        imageUrl
      }
    }
    trackStats(
      ids: $trackIds
      time: $time
      ergTypes: $ergTypes
      startDate: $startDate
      endDate: $endDate
      timezone: $timezone
    ) {
      totalResults
      totalUsers
      totalTime
      totalDistance
      weightedDistance
      totalReactions
      userStats {
        id
        name
        total
      }
    }
    previousTrackStats: trackStats(
      ids: $trackIds
      time: $time
      previousPeriod: true
      ergTypes: $ergTypes
      startDate: $startDate
      endDate: $endDate
      timezone: $timezone
    ) @skip(if: $skipPreviousStats) {
      totalResults
      totalUsers
      totalTime
      totalDistance
      weightedDistance
      totalReactions
      userStats {
        id
        name
        total
      }
    }
  }
`;

interface Params {
  userToken: string;
}

interface TrackStats {
  totalResults: number;
  totalUsers: number;
  totalTime: number;
  totalDistance: number;
  weightedDistance: number;
  totalReactions: number[];
  userStats: UserStat[];
}

interface UserStat {
  id: string;
  name: string;
  total: number[];
}

interface TrackData {
  id: string;
  role: TrackRole;
  source: { id: string; name: string; imageUrl: string; insertedAt: string };
  tracksFromSource: { id: string; name: string; imageUrl: string }[];
}

interface QueryData {
  track: TrackData;
  trackStats: TrackStats;
  previousTrackStats: TrackStats;
}

const muiTheme = createMuiTheme({
  ...muiThemeAttributes,
  palette: {
    ...muiThemeAttributes.palette,
    type: 'dark',
    primary: {
      main: '#009e9a',
      light: '#00C9BE',
      dark: '#007a76',
    },
    secondary: {
      main: '#d9915e',
      light: '#e6a97a',
      dark: '#b3744a',
    },
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
  typography: {
    ...muiThemeAttributes.typography,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
});

const defaultErgs = [
  ErgType.row,
  ErgType.ski,
  ErgType.bike,
  ErgType.fanBike,
  ErgType.run,
  ErgType.multi,
];

const StatItem = ({
  name,
  children,
  color = 'primary',
  style,
  previousValue,
  newValue,
}: {
  name: string;
  children: ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  style?: any;
  previousValue?: number;
  newValue?: number;
}) => {
  const styles = useStyles();

  const getColor = () => {
    switch (color) {
      case 'primary':
        return '#009e9a';
      case 'secondary':
        return '#d9915e';
      case 'success':
        return '#4caf50';
      case 'warning':
        return '#ff9800';
      case 'error':
        return '#f44336';
      default:
        return '#009e9a';
    }
  };

  const percentage = calculateProgressFromPrevious(previousValue, newValue);
  const isIncrease = percentage > 0;

  return (
    <Card className={styles.statCard} style={style} elevation={3}>
      <CardContent className={styles.statCardContent}>
        <Typography variant="h6" className={styles.statTitle}>
          {name}
        </Typography>
        {!!percentage && (
          <Tooltip
            title={
              isIncrease
                ? t(
                    'Increase of %{percentage}% compared to the previous period',
                    { percentage: Math.round(Math.abs(percentage)) }
                  )
                : t(
                    'Decrease of %{percentage}% compared to the previous period',
                    { percentage: Math.round(Math.abs(percentage)) }
                  )
            }
            placement="top"
            arrow
          >
            <Box className={styles.statPercentageHeader}>
              <MaterialIcon
                name={isIncrease ? 'arrow-upward' : 'arrow-downward'}
                size={18}
                color={isIncrease ? green : red}
              />
              {Math.round(Math.abs(percentage))}
              <span style={{ fontSize: 14 }}>%</span>
            </Box>
          </Tooltip>
        )}
        <Typography
          variant="h4"
          className={styles.statValue}
          style={{ color: getColor() }}
        >
          {children}
        </Typography>
      </CardContent>
    </Card>
  );
};

const displayUserDistances = (
  distances: number[],
  previousDistances: number[],
  color: string,
  styles: any
) => {
  return (
    <Box className={styles.userDistancesContainer}>
      {distances.map((distance, index) => {
        const previousDistance =
          previousDistances && previousDistances[index]
            ? previousDistances[index]
            : 0;
        const percentage = calculateProgressFromPrevious(
          previousDistance,
          distance
        );
        const isIncrease = percentage > 0;

        return distance ? (
          <Box key={index} className={styles.userDistanceItem}>
            <ErgIcon ergType={defaultErgs[index]} size={24} color={color} />
            <Typography variant="body2" className={styles.userDistanceText}>
              {humanizeDistance(distance)}
            </Typography>
            {!!percentage && (
              <Tooltip
                title={
                  isIncrease
                    ? t(
                        'Increase of %{percentage}% compared to the previous period',
                        { percentage: Math.round(Math.abs(percentage)) }
                      )
                    : t(
                        'Decrease of %{percentage}% compared to the previous period',
                        { percentage: Math.round(Math.abs(percentage)) }
                      )
                }
                placement="top"
                arrow
              >
                <Box className={styles.userDistancePercentage}>
                  <MaterialIcon
                    name={isIncrease ? 'arrow-upward' : 'arrow-downward'}
                    size={14}
                    color={isIncrease ? green : red}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      color: isIncrease ? green : red,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {Math.round(Math.abs(percentage))}%
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        ) : null;
      })}
    </Box>
  );
};

const UserItem = ({
  stat,
  previousStat,
}: {
  stat: UserStat;
  previousStat?: UserStat;
}) => {
  const styles = useStyles();
  const color = userColor(stat.name);

  return (
    <Card className={styles.userCard} elevation={4}>
      <CardContent className={styles.userCardContent}>
        <Box className={styles.userHeader}>
          <Avatar
            className={styles.userAvatar}
            style={{
              backgroundColor: color,
              width: 48,
              height: 48,
              fontSize: '1.2rem',
              fontWeight: 600,
            }}
          >
            {nameDisplay(stat.name).charAt(0)}
          </Avatar>
          <Typography
            variant="h6"
            className={styles.userName}
            style={{ color }}
          >
            {nameDisplay(stat.name)}
          </Typography>
        </Box>
        <Box className={styles.userStats}>
          {displayUserDistances(
            stat.total,
            previousStat && previousStat.total ? previousStat.total : [],
            color,
            styles
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const humanizeTime = (value: number) => {
  const duration = moment.duration(value * 1000);
  const years = duration.years();
  const months = duration.months();
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  const values = [];

  if (years) {
    values.push(`${years}y`);
    values.push(`${months}m`);
    values.push(`${days}d`);
  } else if (months) {
    values.push(`${months}m`);
    values.push(`${days}d`);
  } else if (days) {
    values.push(`${days}d`);
    if (hours) values.push(`${hours}h`);
  } else {
    if (hours) values.push(`${hours}h`);
    if (minutes) values.push(`${minutes}m`);
  }

  return values.join(' ');
};

const getYears = (insertedAt: string) => {
  const start = moment(insertedAt).year();
  const end = moment().year();
  return Array.from(Array(end - start + 1).keys())
    .map((i) => start + i)
    .reverse();
};

interface Params {
  trackId?: string;
}

const TrackStatsPage = () => {
  const styles = useStyles();
  const [time, setTime] = useState(isMobileApp() ? '7-day' : '30-day');
  const { trackId: trackIdFromParam } = useParams<Params>();
  const [trackIds, setTrackIds] = useState([trackIdFromParam]);
  const [ergTypes, setErgTypes] = useState<ErgType[]>([]);
  const [customDateRange, setCustomDateRange] = useState<DateRange>();
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);

  const variables = {
    trackId: trackIdFromParam,
    trackIds,
    time,
    ...(time === 'custom' ? customDateRange : {}),
    ergTypes: ergTypes.length === 0 ? defaultErgs : ergTypes,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    skipPreviousStats: time === 'lifetime',
  };

  const isAllErgTypes =
    ergTypes.length === 0 || ergTypes.length === defaultErgs.length;

  useEffect(() => {
    document.body.style.backgroundColor = bgColor;
    setTimeout(() => postMobileMessage('loaded'), 100);
  }, []);

  const { data, loading } = useQuery<QueryData>(GET_STATS, {
    fetchPolicy: 'cache-and-network',
    variables,
    pollInterval: 10 * 60 * 1000,
  });

  const track = data && data.track;
  const trackStats = data ? data.trackStats : null;
  const previousTrackStats = data ? data.previousTrackStats : null;

  const stats = trackStats ? trackStats.userStats : [];
  const totalDistance = trackStats ? trackStats.totalDistance : 0;
  const totalResults = trackStats ? trackStats.totalResults : 0;
  const totalUsers = trackStats ? trackStats.totalUsers : 0;
  const totalTime = trackStats ? trackStats.totalTime : 0;
  const totalReactions = trackStats ? trackStats.totalReactions : [0, 0, 0, 0];
  const pace = trackStats
    ? calculatePace(
        trackStats.totalTime,
        trackStats.weightedDistance,
        ErgType.row
      )
    : 0;
  const avgCalories =
    trackStats && pace ? calculateCalsHour(calculatePower(pace)) : 0;

  // Previous period stats
  const previousTotalDistance = previousTrackStats
    ? previousTrackStats.totalDistance
    : 0;
  const previousTotalResults = previousTrackStats
    ? previousTrackStats.totalResults
    : 0;
  const previousTotalUsers = previousTrackStats
    ? previousTrackStats.totalUsers
    : 0;
  const previousTotalTime = previousTrackStats
    ? previousTrackStats.totalTime
    : 0;
  const previousPace = previousTrackStats
    ? calculatePace(
        previousTrackStats.totalTime,
        previousTrackStats.weightedDistance,
        ErgType.row
      )
    : 0;
  const previousAvgCalories =
    previousTrackStats && previousPace
      ? calculateCalsHour(calculatePower(previousPace))
      : 0;

  const previousUserStatsMap = previousTrackStats
    ? previousTrackStats.userStats.reduce(
        (acc: Record<string, UserStat>, stat) => {
          const id = stat.id.replace(/^p-/, '');
          acc[id] = stat;
          return acc;
        },
        {}
      )
    : {};

  const years = track ? getYears(track.source.insertedAt) : [];
  const showOlderTimeframes = !!track && track.role === TrackRole.Admin;

  const TrackItem = ({
    name,
    imageUrl,
  }: {
    name: string;
    imageUrl: string;
  }) => {
    return (
      <Box className={styles.trackItemContainer}>
        <Avatar className={styles.trackAvatar} src={imageUrl} />
        <Typography className={styles.trackName}>{name}</Typography>
      </Box>
    );
  };

  const LoadingSkeleton = () => (
    <Box className={styles.skeletonContainer}>
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={6} sm={6} md={3} key={i}>
            <Card className={styles.statCard} elevation={3}>
              <CardContent>
                <Box
                  style={{
                    height: 24,
                    width: '60%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    marginBottom: 8,
                    borderRadius: 4,
                  }}
                />
                <Box
                  style={{
                    height: 32,
                    width: '80%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <>
      {loading && (
        <ProgressBar
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
          indeterminate
        />
      )}
      <Box className={styles.container}>
        <Fade in={!!data} timeout={500}>
          <Box className={styles.content}>
            {/* Header Section */}
            <Card className={styles.headerCard} elevation={4}>
              <CardContent className={styles.headerContent}>
                <Box className={styles.headerTop}>
                  {!!track && !isMobileApp() && (
                    <Box className={styles.trackSelector}>
                      <FormControl
                        variant="outlined"
                        className={styles.trackFormControl}
                      >
                        <InputLabel className={styles.trackLabel}>
                          {t('Select Track')}
                        </InputLabel>
                        <Select
                          labelId="track-label"
                          value={trackIds.length === 1 ? trackIds[0] : 'all'}
                          onChange={(event) => {
                            const value = event.target.value as string;
                            setTrackIds(
                              value === 'all'
                                ? track.tracksFromSource.map((t) => t.id)
                                : [value]
                            );
                          }}
                          label={t('Select Track')}
                          className={styles.trackSelect}
                        >
                          {track.tracksFromSource.length > 1 && (
                            <MenuItem value="all">
                              <TrackItem
                                name={`${track.source.name}: ${t('All')}`}
                                imageUrl={track.source.imageUrl}
                              />
                            </MenuItem>
                          )}
                          {track.tracksFromSource.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              <TrackItem name={t.name} imageUrl={t.imageUrl} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Box className={styles.filtersContainer}>
                    <Box className={styles.ergFilters}>
                      <FormControl
                        variant="outlined"
                        className={styles.ergFormControl}
                      >
                        <InputLabel className={styles.ergLabel}>
                          {t('Erg Types')}
                        </InputLabel>
                        <Select
                          multiple
                          value={isAllErgTypes ? ['all'] : ergTypes}
                          onChange={(event) => {
                            const value = (
                              event.target.value as string[]
                            ).filter((v) => v !== 'all');
                            setErgTypes(value as ErgType[]);
                          }}
                          label={t('Erg Types')}
                          className={styles.ergSelect}
                          renderValue={(selected: any) => {
                            const selectedValues = selected as string[];

                            if (isAllErgTypes) {
                              return (
                                <Typography
                                  variant="body2"
                                  style={{ marginLeft: 8 }}
                                >
                                  {t('All')}
                                </Typography>
                              );
                            }

                            return (
                              <Box className={styles.chipContainer}>
                                {selectedValues.map((value) => (
                                  <Chip
                                    key={value}
                                    label={
                                      <Box className={styles.chipContent}>
                                        <ErgIcon
                                          ergType={value as ErgType}
                                          size={14}
                                          color="#ffffff"
                                        />
                                        <span style={{ marginLeft: 4 }}>
                                          {ergTypeDisplay(value as ErgType)}
                                        </span>
                                      </Box>
                                    }
                                    className={styles.chip}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          {defaultErgs.map((ergType) => (
                            <MenuItem key={ergType} value={ergType}>
                              <Box className={styles.menuItemContent}>
                                <ErgIcon
                                  ergType={ergType}
                                  size={16}
                                  color="#ffffff"
                                />
                                <Typography
                                  variant="body2"
                                  style={{ marginLeft: 8 }}
                                >
                                  {ergTypeDisplay(ergType)}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box className={styles.timeframeSelector}>
                      <FormControl
                        variant="outlined"
                        className={styles.timeframeFormControl}
                      >
                        <InputLabel className={styles.timeframeLabel}>
                          {t('Time Period')}
                        </InputLabel>
                        <Select
                          labelId="timeframe-label"
                          value={time}
                          onChange={(event) => {
                            if (event.target.value !== 'custom') {
                              setTime(event.target.value as string);
                            }
                          }}
                          label={t('Time Period')}
                          className={styles.timeframeSelect}
                        >
                          <MenuItem value="1-hour">{t('Past hour')}</MenuItem>
                          <MenuItem value="1-day">{t('Past day')}</MenuItem>
                          <MenuItem value="7-day">{t('Past 7 days')}</MenuItem>
                          <MenuItem value="30-day">
                            {t('Past 30 days')}
                          </MenuItem>
                          {showOlderTimeframes && (
                            <MenuItem value="365-day">
                              {t('Past 365 days')}
                            </MenuItem>
                          )}
                          {(showOlderTimeframes ? years : []).map((year) => (
                            <MenuItem key={year} value={`year-${year}`}>
                              {year}
                            </MenuItem>
                          ))}
                          {showOlderTimeframes && (
                            <MenuItem value="lifetime">
                              {t('Lifetime')}
                            </MenuItem>
                          )}
                          {showOlderTimeframes && (
                            <MenuItem
                              value="custom"
                              onClick={() => setShowDateRangeModal(true)}
                            >
                              {customDateRangeDisplay(customDateRange)}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {!isMobileApp() && (
                    <Box className={styles.poweredBy}>
                      <Typography
                        variant="body2"
                        className={styles.poweredByText}
                      >
                        Powered by
                      </Typography>
                      <Logo size={24} darkMode />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            {loading ? (
              <LoadingSkeleton />
            ) : (
              <Box className={styles.statsSection}>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {t('Overall Stats')}
                </Typography>
                <Grid container spacing={3} className={styles.statsGrid}>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Results')}
                      color="primary"
                      previousValue={previousTotalResults}
                      newValue={totalResults}
                    >
                      {totalResults ? totalResults.toLocaleString() : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Athletes')}
                      color="secondary"
                      previousValue={previousTotalUsers}
                      newValue={totalUsers}
                    >
                      {totalUsers ? totalUsers.toLocaleString() : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Distance')}
                      color="success"
                      previousValue={previousTotalDistance}
                      newValue={totalDistance}
                    >
                      {totalDistance ? humanizeDistance(totalDistance) : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Time')}
                      color="warning"
                      previousValue={previousTotalTime}
                      newValue={totalTime}
                    >
                      {totalTime ? humanizeTime(totalTime) : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Average Pace')}
                      color="primary"
                      previousValue={
                        previousPace && calculatePower(previousPace)
                      }
                      newValue={pace && calculatePower(pace)}
                    >
                      {pace ? formatTime(pace, true) : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Average Watts')}
                      color="secondary"
                      previousValue={
                        previousPace && calculatePower(previousPace)
                      }
                      newValue={pace && calculatePower(pace)}
                    >
                      {pace ? calculatePower(pace).toFixed(0) : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Average Cals/hr')}
                      color="error"
                      previousValue={previousAvgCalories}
                      newValue={avgCalories}
                    >
                      {avgCalories
                        ? humanizeDistance(avgCalories, { meters: '' })
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem name={t('Total Reactions')} color="success">
                      <Box className={styles.reactionsContainer}>
                        {statsEmojis.map((emoji, index) => (
                          <Chip
                            key={emoji}
                            label={`${emoji} ${totalReactions[index]}`}
                            className={styles.reactionChip}
                            size="small"
                          />
                        ))}
                      </Box>
                    </StatItem>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* User Stats Section */}
            {!loading && stats.length > 0 && (
              <Box className={styles.usersSection}>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {t('Athlete Performance')}
                </Typography>
                <Typography variant="body1" className={styles.sectionSubtitle}>
                  {t('Individual athlete distances by erg type')}
                </Typography>
                <Box className={styles.usersGrid}>
                  {stats.map((stat, i) => {
                    const previousStat = previousUserStatsMap[stat.id];
                    return (
                      <UserItem
                        key={i}
                        stat={stat}
                        previousStat={previousStat}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Box>

      {showDateRangeModal && (
        <DateRangeModal
          minDate={moment().subtract(100, 'years')}
          maxDate={moment()}
          initialDateRange={
            customDateRange || {
              startDate: moment().subtract('7', 'days').format('YYYY-MM-DD'),
              endDate: moment().format('YYYY-MM-DD'),
            }
          }
          onDismiss={() => {
            setShowDateRangeModal(false);
          }}
          onComplete={(range) => {
            setTime('custom');
            setShowDateRangeModal(false);
            setCustomDateRange(range);
          }}
        />
      )}
    </>
  );
};

const nameDisplay = (name: string) => {
  const [first, last] = name.split(' ');
  return `${first} ${last ? last[0] : ''}`;
};

const bgColor = '#1a1a1a';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${bgColor} 0%, #2d2d2d 100%)`,
      padding: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    },
    content: {
      maxWidth: 1400,
      margin: '0 auto',
    },
    headerCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      marginBottom: theme.spacing(4),
    },
    headerContent: {
      padding: theme.spacing(3),
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: `${theme.spacing(2)}px`,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    trackSelector: {
      flex: 1,
      minWidth: 200,
    },
    trackFormControl: {
      width: '100%',
    },
    trackLabel: {
      color: theme.palette.text.secondary,
    },
    trackSelect: {
      color: theme.palette.text.primary,
      '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
      },
    },
    trackItemContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(1)}px`,
    },
    trackAvatar: {
      width: 32,
      height: 32,
    },
    trackName: {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
    filtersContainer: {
      flex: 1,
      minWidth: 200,
      display: 'flex',
      gap: `${theme.spacing(3)}px`,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: `${theme.spacing(2)}px`,
      },
    },
    ergFilters: {
      flex: 1,
      minWidth: 200,
    },
    ergFormControl: {
      width: '100%',
    },
    ergLabel: {
      color: theme.palette.text.secondary,
    },
    ergSelect: {
      color: theme.palette.text.primary,
      '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
      },
    },
    timeframeSelector: {
      flex: 1,
      minWidth: 200,
    },
    timeframeFormControl: {
      width: '100%',
    },
    timeframeLabel: {
      color: theme.palette.text.secondary,
    },
    timeframeSelect: {
      color: theme.palette.text.primary,
      '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
      },
    },
    poweredBy: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(1)}px`,
    },
    poweredByText: {
      color: theme.palette.text.secondary,
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${theme.spacing(0.5)}px`,
    },
    chipContent: {
      display: 'flex',
      alignItems: 'center',
    },
    chip: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 12,
      padding: theme.spacing(0.5),
    },
    menuItemContent: {
      display: 'flex',
      alignItems: 'center',
    },
    skeletonContainer: {
      marginTop: theme.spacing(4),
    },
    statsSection: {
      marginBottom: theme.spacing(6),
    },
    sectionTitle: {
      color: theme.palette.text.primary,
      marginBottom: `${theme.spacing(3)}px`,
      fontWeight: 600,
    },
    sectionSubtitle: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(4),
    },
    statsGrid: {
      marginBottom: theme.spacing(4),
    },
    statCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
    },
    statCardContent: {
      padding: theme.spacing(3),
      minHeight: 60,
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
    },
    statTitle: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    statValue: {
      fontWeight: 700,
      fontSize: '1.5rem',
      wordBreak: 'break-word',
      marginTop: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '2.25rem',
      },
    },
    reactionsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${theme.spacing(0.5)}px`,
    },
    reactionChip: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: theme.palette.text.primary,
      border: '1px solid rgba(255, 255, 255, 0.2)',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
      },
    },
    usersSection: {
      marginTop: theme.spacing(6),
    },
    usersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: `${theme.spacing(3)}px`,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: `${theme.spacing(1)}px`,
      },
    },
    userCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
      marginBottom: theme.spacing(3),
    },
    userCardContent: {
      padding: theme.spacing(3),
    },
    userHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(2)}px`,
      marginBottom: theme.spacing(2),
    },
    userAvatar: {
      fontWeight: 600,
    },
    userName: {
      fontWeight: 600,
      flex: 1,
    },
    userStats: {
      marginTop: theme.spacing(2),
    },
    userDistancesContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
      gap: `${theme.spacing(2)}px`,
    },
    userDistanceItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: `${theme.spacing(0.5)}px`,
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      background: 'rgba(255, 255, 255, 0.05)',
    },
    userDistanceText: {
      textAlign: 'center',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    userDistancePercentage: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(0.5)}px`,
      marginTop: theme.spacing(0.5),
    },
    statPercentageHeader: {
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      borderRadius: theme.shape.borderRadius,
      background: 'rgba(255, 255, 255, 0.1)',
      fontSize: '0.875rem',
      fontWeight: 500,
      [theme.breakpoints.down('sm')]: {
        top: theme.spacing(1),
        right: theme.spacing(1),
        padding: `${theme.spacing(0.25)}px ${theme.spacing(0.5)}px`,
        fontSize: '0.75rem',
        zIndex: 1,
      },
    },
  })
);

export default function TrackStatsPageWrapper() {
  return (
    <ThemeProvider theme={muiTheme}>
      <TrackStatsPage />
    </ThemeProvider>
  );
}
